import { AbstractControl, ValidatorFn } from '@angular/forms';
import { hasProperty, isRecord } from '@fmnts/core';
import { includedIn } from '@fmnts/core/collection';

export interface IncludesExactlyValidatorArgs {
  /**
   * List of values that must be included.
   */
  values: unknown[];
}

export interface IncludesExactlyValidationError {
  /** Values that are required by the validator. */
  requiredValues: unknown[];
  /**
   * Actual control values.
   * When used with a control that doesn't have a collection
   * of values, the value will be wrapped in an array.
   */
  actualValues: unknown[];
}

/**
 * @param e Validation errors object
 * @returns
 * `true` if the given validation errors object contains
 * errors from the {@link includesExactlyValidator}.
 */
export const hasIncludesExactlyError = hasProperty(
  'includesExactly',
  (u): u is IncludesExactlyValidationError => isRecord(u),
);

/**
 * Creates a validation function that tests the value of a form control
 * against a given set of values. Every value must be present in the
 * current control value in order to be valid. It must not contain
 * more or less values.
 *
 * This works with both, controls that use an array as their value as
 * well as controls with a primitive value.
 *
 * It uses {@link Array.prototype.includes} to check if every value is included,
 * which means that it only works with primitive values.
 *
 * @param args Validator options
 *
 * @returns
 * Validator function.
 */
export function includesExactlyValidator(
  args: IncludesExactlyValidatorArgs,
): ValidatorFn {
  const { values: requiredValues } = args;

  return (
    control: AbstractControl<unknown>,
  ): Record<'includesExactly', IncludesExactlyValidationError> | null => {
    const { value } = control;
    // Make sure that the value is an array
    const actualValues = Array.isArray(value) ? value : [value];

    // Every value must be included exactly once
    if (
      requiredValues.length !== actualValues.length ||
      !requiredValues.every(includedIn(actualValues))
    ) {
      return {
        includesExactly: {
          actualValues,
          requiredValues,
        },
      };
    }

    return null;
  };
}
